/**
 * V1e · Page admin — Queue de modération des credentials.
 *
 * Workflow :
 *   1. L'admin arrive sur `/credentials/moderation`
 *   2. Tabs filtrent par status (PENDING par défaut, FIFO)
 *   3. Filtre type optionnel (DIPLOMA / CERTIFICATION / ORDER_NUMBER / PROFESSIONAL_LICENSE)
 *   4. Click "Modérer" sur une ligne → ReviewModal
 *   5. Décision soumise → table refetch automatique
 *
 * Pagination simple côté client (TablePagination MUI). V1e2 si volume
 * augmente : pagination serveur + cursor-based.
 */

import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Title, useNotify } from 'react-admin';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { GET_CREDENTIAL_VERIFICATION_QUEUE } from './credentials.queries';
import {
  CredentialReviewModal,
} from './CredentialReviewModal';
import { CredentialStatsPanel } from './CredentialStatsPanel';
import {
  CredentialVerification,
  CredentialVerificationStatus,
  CredentialVerificationType,
  CredentialVerificationQueueResponse,
} from './credentials.types';

const STATUS_TABS: Array<{ value: CredentialVerificationStatus; label: string; color: string }> = [
  { value: CredentialVerificationStatus.PENDING, label: 'En attente', color: '#F59E0B' },
  { value: CredentialVerificationStatus.UNDER_REVIEW, label: 'En cours d\'examen', color: '#3B82F6' },
  { value: CredentialVerificationStatus.APPROVED, label: 'Approuvées', color: '#10B981' },
  { value: CredentialVerificationStatus.REJECTED, label: 'Refusées', color: '#EF4444' },
];

const TYPE_LABELS: Record<CredentialVerificationType, string> = {
  [CredentialVerificationType.DIPLOMA]: 'Diplôme',
  [CredentialVerificationType.CERTIFICATION]: 'Certification',
  [CredentialVerificationType.ORDER_NUMBER]: 'N° Ordre',
  [CredentialVerificationType.PROFESSIONAL_LICENSE]: 'Licence pro',
};

const PAGE_SIZE = 25;

export function CredentialModerationPage() {
  const notify = useNotify();
  const [statusTab, setStatusTab] = useState<CredentialVerificationStatus>(
    CredentialVerificationStatus.PENDING,
  );
  const [typeFilter, setTypeFilter] = useState<CredentialVerificationType | ''>('');
  const [page, setPage] = useState(0);
  const [reviewVerification, setReviewVerification] =
    useState<CredentialVerification | null>(null);

  const { data, loading, error, refetch } = useQuery<{
    credentialVerificationQueue: CredentialVerificationQueueResponse;
  }>(GET_CREDENTIAL_VERIFICATION_QUEUE, {
    variables: {
      status: statusTab,
      type: typeFilter || undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const items = data?.credentialVerificationQueue.items ?? [];
  const total = data?.credentialVerificationQueue.total ?? 0;

  const handleTabChange = (_: React.SyntheticEvent, newValue: CredentialVerificationStatus) => {
    setStatusTab(newValue);
    setPage(0);
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
    setTypeFilter(event.target.value as CredentialVerificationType | '');
    setPage(0);
  };

  const handleReviewed = () => {
    void refetch();
    notify('Liste rafraîchie', { type: 'info' });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Title title="Modération des credentials" />

      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Modération des credentials LIBERAL
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Examinez les pièces justificatives soumises par les libéraux
          (médecins, avocats, notaires, experts comptables) et validez ou
          refusez la vérification de leurs diplômes / certifications / N° d'ordre.
        </Typography>
      </Box>

      {/* V1e+ · Stats panel — volume queue + temps moyen + top reviewers */}
      <CredentialStatsPanel windowDays={30} />

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={statusTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          data-testid="credential-moderation-tabs"
        >
          {STATUS_TABS.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={tab.label}
              data-testid={`credential-moderation-tab-${tab.value}`}
            />
          ))}
        </Tabs>
      </Paper>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filtre type</InputLabel>
            <Select
              label="Filtre type"
              value={typeFilter}
              onChange={handleTypeChange as any}
              data-testid="credential-moderation-type-filter"
            >
              <MenuItem value="">Tous</MenuItem>
              {Object.values(CredentialVerificationType).map((t) => (
                <MenuItem key={t} value={t}>
                  {TYPE_LABELS[t]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            startIcon={<RefreshIcon />}
            onClick={() => void refetch()}
            data-testid="credential-moderation-refresh"
          >
            Rafraîchir
          </Button>

          <Box sx={{ flex: 1 }} />

          <Typography variant="body2" color="text.secondary">
            <strong>{total}</strong> demande{total > 1 ? 's' : ''} dans cette vue
          </Typography>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erreur de chargement : {error.message}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small" data-testid="credential-moderation-table">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Field Ref</TableCell>
              <TableCell>Soumissionnaire</TableCell>
              <TableCell>Soumis le</TableCell>
              <TableCell>Pièce</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid="credential-moderation-empty"
                  >
                    Aucune demande dans cette catégorie.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  data-testid={`credential-moderation-row-${item.id}`}
                >
                  <TableCell>
                    <Chip
                      label={TYPE_LABELS[item.type]}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <code style={{ fontSize: 12 }}>{item.fieldRef}</code>
                  </TableCell>
                  <TableCell>
                    <code style={{ fontSize: 11 }}>
                      {item.userId.slice(0, 8)}…
                    </code>
                  </TableCell>
                  <TableCell>
                    {new Date(item.submittedAt).toLocaleString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </TableCell>
                  <TableCell>
                    {item.documentUrl ? (
                      <Chip
                        label={
                          item.documentMimeType === 'application/pdf'
                            ? 'PDF'
                            : item.documentMimeType?.startsWith('image/')
                              ? 'Image'
                              : 'Doc'
                        }
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Chip label="Aucune" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.status}
                      size="small"
                      sx={{
                        bgcolor:
                          STATUS_TABS.find((t) => t.value === item.status)?.color,
                        color: '#fff',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => setReviewVerification(item)}
                      data-testid={`credential-moderation-review-button-${item.id}`}
                    >
                      Modérer
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      </TableContainer>

      <CredentialReviewModal
        verification={reviewVerification}
        open={Boolean(reviewVerification)}
        onClose={() => setReviewVerification(null)}
        onReviewed={handleReviewed}
      />
    </Box>
  );
}
